import { XMarkIcon } from '@heroicons/react/24/outline';

import { StatusChip } from '@/components/legacy-ui/StatusChip';
import { Button } from '@/components/ui/button';
import { parseProgress } from '@/hooks/useBulkJobs/parseProgress';
import { type BulkJobRecord } from '@/state/bulkJobs.store';

import { humanizeAction } from './actionLabels';
import { BulkEntityIconCircle } from './BulkEntityIconCircle';

/** Pure helper — takes current timestamp to avoid impure Date.now() in render. */
function getRelativeTime(submittedAt: number, nowMs: number): string {
  const elapsed = Math.floor((nowMs - submittedAt) / 1000);
  if (elapsed < 60) return `${elapsed}s ago`;
  if (elapsed < 3600) return `${Math.floor(elapsed / 60)}m ago`;
  return `${Math.floor(elapsed / 3600)}h ago`;
}

function statusVariant(
  status: BulkJobRecord['status']
): 'muted' | 'high' | 'info' | 'critical' | 'default' {
  switch (status) {
    case 'queued':
      return 'muted';
    case 'running':
      return 'high';
    case 'pass':
      return 'info';
    case 'fail':
      return 'critical';
    case 'partial':
      return 'high';
  }
}

function statusLabel(status: BulkJobRecord['status']): string {
  switch (status) {
    case 'queued':
      return 'Queued';
    case 'running':
      return 'Running';
    case 'pass':
      return 'Completed';
    case 'fail':
      return 'Failed';
    case 'partial':
      return 'Partial';
  }
}

interface BulkJobRowProps {
  job: BulkJobRecord;
  onDismiss: (jobId: string) => void;
  /** Injected render timestamp for testability. Defaults to job.submittedAt-based display. */
  nowMs?: number;
}

export function BulkJobRow({ job, onDismiss, nowMs }: BulkJobRowProps) {
  const progress = parseProgress(job.comment);
  const isActive = job.status === 'queued' || job.status === 'running';

  // nowMs is passed in by caller (avoids impure Date.now() during render per React compiler rules)
  const relativeTime = getRelativeTime(
    job.submittedAt,
    nowMs ?? job.submittedAt
  );

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <BulkEntityIconCircle resource={job.resource} />

      <div className="min-w-0 flex-1">
        {/* Primary label */}
        <p className="text-chariot-text-primary text-sm font-medium">
          {humanizeAction(job.action)} {job.resource}s
        </p>

        {/* Progress bar when running */}
        {isActive && progress && (
          <div className="mt-1.5 space-y-0.5">
            {(() => {
              const percent =
                progress.total > 0
                  ? Math.round((progress.processed / progress.total) * 100)
                  : 0;
              return (
                <div
                  role="progressbar"
                  aria-valuenow={percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${humanizeAction(job.action)} progress: ${percent}%`}
                  className="bg-chariot-stroke h-1 w-full overflow-hidden rounded-full"
                >
                  <div
                    className="bg-chariot-brand-primary h-1 rounded-full transition-all duration-500"
                    style={{ width: `${percent}%` }}
                  />
                </div>
              );
            })()}
            <p className="text-chariot-text-secondary text-xs">
              {progress.processed}/{progress.total}
              {progress.failed > 0 && (
                <span className="text-chariot-status-critical ml-1">
                  ({progress.failed} failed)
                </span>
              )}
            </p>
          </div>
        )}

        {/* Status + timestamp */}
        <div className="mt-1 flex items-center gap-2">
          <StatusChip
            label={statusLabel(job.status)}
            variant={statusVariant(job.status)}
          />
          <span className="text-chariot-text-secondary text-xs">
            {relativeTime}
          </span>
        </div>
      </div>

      {/* Reserved cancel slot (w-16) + dismiss button */}
      <div className="flex shrink-0 items-center gap-1">
        {/* w-16 reserved for future cancel affordance (per plan §2.6) */}
        <div className="w-0" aria-hidden="true" />
        {!isActive && (
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            onClick={() => onDismiss(job.jobId)}
            aria-label={`Dismiss ${humanizeAction(job.action)} ${job.resource}s job`}
          >
            <XMarkIcon className="size-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
